import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CompanyCycleApiService } from '../../empresas/services/company-cycle-api.service';
import { CompanyCycleRequest } from '../../empresas/models/empresa.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-company-cycle-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './company-cycle-form.component.html',
})
export class CompanyCycleFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form model. displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  name = '';
  private originalName = '';
  description = '';
  triggerAction: 'NONE' | 'CREATE_DEAL' | 'CREATE_LEAD' = 'NONE';
  isActive = true;

  // Input validation
  nameValidationError = '';

  readonly NAME_MAX = 30;

  constructor(
    private api: CompanyCycleApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  onNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
    filtered = filtered.substring(0, this.NAME_MAX);

    if (filtered !== value) {
      this.nameValidationError = 'Solo se permiten letras y espacios';
    } else {
      this.nameValidationError = '';
    }

    // delay mínimo para forzar override de ngModel
    setTimeout(() => {
      this.name = filtered;
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.api.getById(id, businessId).subscribe({
      next: (ciclo) => {
        this.name = ciclo.name;
        this.originalName = ciclo.name;
        this.description = ciclo.description || '';
        this.triggerAction = ciclo.triggerAction ?? 'NONE';
        this.isActive = ciclo.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el ciclo';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Ciclo de Empresa'
      : 'Nuevo Ciclo de Empresa';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const request: CompanyCycleRequest = {
      businessId,
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      triggerAction: this.triggerAction,
      isActive: this.isActive,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Ciclo',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/ciclos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el ciclo';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Ciclo', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/ciclos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el ciclo';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-empresas/ciclos']);
  }
}
